import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Input, Table, Tag, Typography } from 'antd';
// Api
import { GET_USER_FEATURE_LIMITS_LIST } from 'api/featureLimits/queries';
import { FeatureLimitKey, FeatureLimitKind } from 'api/graphql-global-types';
// Helpers
import {
  FEATURE_LIMIT_LABELS,
  TOGGLE_STATE_LABELS,
  formatLimit,
} from '../FeatureLimits/constants';
// Components
import UserFeatureLimitsDrawer from '../FeatureLimits/components/UserFeatureLimitsDrawer/UserFeatureLimitsDrawer';
// Styles
import styles from './ActiveFeatureLimits.module.scss';

const { Text, Title } = Typography;

const PAGE_SIZE = 20;

type Override = {
  featureKey: FeatureLimitKey;
  monthlyLimit: number | null;
  kind: FeatureLimitKind;
};

type UserGroup = {
  userId: string;
  userName: string;
  userEmail: string | null;
  updatedAt: string;
  overrides: Override[];
};

type SelectedUser = {
  userId: string;
  name: string;
} | null;

const overrideText = (override: Override): string => {
  const label =
    FEATURE_LIMIT_LABELS[override.featureKey] ?? override.featureKey;
  if (override.kind === FeatureLimitKind.Toggle) {
    const stateLabels = TOGGLE_STATE_LABELS[override.featureKey];
    return `${label}: ${
      override.monthlyLimit === 0 ? stateLabels?.off : stateLabels?.on
    }`;
  }
  return `${label}: ${formatLimit(override.monthlyLimit)}`;
};

const ActiveFeatureLimits: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<SelectedUser>(null);

  const { data, loading, refetch } = useQuery(GET_USER_FEATURE_LIMITS_LIST, {
    variables: {
      input: {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        ...(searchTerm ? { searchTerm } : {}),
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const result = data?.userFeatureLimitsList;
  const entities: UserGroup[] = result?.entities ?? [];
  const total: number = result?.total ?? 0;

  const columns = [
    {
      title: 'User',
      key: 'user',
      width: 260,
      render: (_: unknown, record: UserGroup) => (
        <div className={styles.userCell}>
          <Text>{record.userName}</Text>
          {record.userEmail ? (
            <Text type="secondary" className={styles.userEmail}>
              {record.userEmail}
            </Text>
          ) : null}
        </div>
      ),
    },
    {
      title: 'Active limits',
      key: 'overrides',
      render: (_: unknown, record: UserGroup) => (
        <div className={styles.overridesCell}>
          {record.overrides.map((override) => (
            <Tag
              key={override.featureKey}
              color={override.monthlyLimit === 0 ? 'red' : 'blue'}
            >
              {overrideText(override)}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: 'Last updated',
      key: 'updatedAt',
      width: 180,
      render: (_: unknown, record: UserGroup) =>
        new Date(record.updatedAt).toLocaleString(),
    },
  ];

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Title level={4}>Active Feature Limits</Title>
        <Text type="secondary">
          Users with per-user overrides currently in effect — one row per user.
          Click a row to manage that user&apos;s limits.
        </Text>
      </div>

      <div className={styles.filters}>
        <Input.Search
          allowClear
          placeholder="Search by name or email"
          style={{ width: 320 }}
          onSearch={(value) => {
            setSearchTerm(value.trim());
            setPage(1);
          }}
        />
      </div>

      <Table<UserGroup>
        rowKey="userId"
        columns={columns}
        dataSource={entities}
        loading={loading && !data}
        onRow={(record) => ({
          onClick: () =>
            setSelectedUser({ userId: record.userId, name: record.userName }),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total,
          onChange: setPage,
          showSizeChanger: false,
        }}
      />

      <UserFeatureLimitsDrawer
        userId={selectedUser?.userId ?? null}
        userName={selectedUser?.name ?? ''}
        open={!!selectedUser}
        onClose={() => {
          setSelectedUser(null);
          refetch();
        }}
      />
    </div>
  );
};

export default ActiveFeatureLimits;
